import { Component } from '@theme/component';
import {
  debounce,
  isClickedOutside,
  lockScroll,
  onAnimationEnd,
  restoreMobileHeaderShell,
  unlockScroll,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#searchOpeningRevision += 1;
    this.#cancelMobileSearchHeaderActionAnimations();
    this.#clearMobileSearchGeometry(this.refs.dialog);
    this.#restoreHeaderShellOnMobileSearchClose = false;
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #searchOpeningRevision = 0;
  #mobileSearchHeaderActionAnimations = [];
  #mobileSearchHeaderActionStates = new Map();
  #restoreHeaderShellOnMobileSearchClose = false;

  #isMobileSearch() {
    return this.id === 'search-modal' && window.matchMedia('(max-width: 749px)').matches;
  }

  /**
   * Match the search surface's collapsed edge to the real mobile header
   * instead of opening it from a hard-coded height.
   * @param {HTMLDialogElement} dialog
   */
  #setMobileSearchGeometry(dialog) {
    const searchRow = document.querySelector(
      '#header-component .header__row--top .search-action--hidden-on-menu'
    );
    if (!(searchRow instanceof HTMLElement)) return;

    const searchControl = searchRow.querySelector('.header-actions__action');
    if (!(searchControl instanceof HTMLElement)) return;

    const rowRect = searchRow.getBoundingClientRect();
    const controlRect = searchControl.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    if (
      rowRect.width <= 0 ||
      rowRect.height <= 0 ||
      controlRect.width <= 0 ||
      controlRect.height <= 0 ||
      viewportWidth <= 0 ||
      viewportHeight <= 0
    ) {
      return;
    }

    // Do not round the measured control geometry. Mobile Safari frequently
    // positions the sticky header on a fractional device pixel; rounding here
    // made the modal input settle by that fraction at the end of the animation.
    const toCssPixels = (value) => `${value.toFixed(3)}px`;

    dialog.style.setProperty('--stylist-mobile-search-input-top', toCssPixels(controlRect.top));
    dialog.style.setProperty('--stylist-mobile-search-input-left', toCssPixels(controlRect.left));
    dialog.style.setProperty(
      '--stylist-mobile-search-input-right',
      toCssPixels(Math.max(0, viewportWidth - controlRect.right))
    );
    dialog.style.setProperty('--stylist-mobile-search-origin-height', toCssPixels(rowRect.bottom));
    dialog.style.setProperty(
      '--stylist-mobile-search-panel-height',
      `${Math.min(Math.round(viewportHeight * 0.66), 560)}px`
    );
    dialog.style.setProperty(
      '--stylist-mobile-search-results-height',
      `${Math.min(Math.round(viewportHeight * 0.78), 640)}px`
    );
    dialog.style.setProperty(
      '--stylist-mobile-search-max-height',
      `${Math.max(Math.round(viewportHeight) - 4, 1)}px`
    );
  }

  /**
   * Remove the opening snapshot so the next invocation is measured against
   * the current header and viewport.
   * @param {HTMLDialogElement} dialog
   */
  #clearMobileSearchGeometry(dialog) {
    [
      '--stylist-mobile-search-input-top',
      '--stylist-mobile-search-input-left',
      '--stylist-mobile-search-input-right',
      '--stylist-mobile-search-origin-height',
      '--stylist-mobile-search-panel-height',
      '--stylist-mobile-search-results-height',
      '--stylist-mobile-search-max-height',
    ].forEach((property) => dialog.style.removeProperty(property));
  }

  #cancelMobileSearchHeaderActionAnimations() {
    this.#mobileSearchHeaderActionAnimations.forEach((animation) => animation.cancel());
    this.#mobileSearchHeaderActionAnimations = [];
  }

  /**
   * Give the existing BAG and MENU controls a restrained handoff to and from
   * the mobile Search surface. The animation owns only opacity/translation;
   * it never changes the header state, page scroll or dialog focus.
   * @param {boolean} visible
   */
  #animateMobileSearchHeaderActions(visible) {
    if (!this.#isMobileSearch()) return;

    this.#cancelMobileSearchHeaderActionAnimations();

    const elements = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll(
        '#header-component header-actions, #header-component .header__drawer .header__icon--summary'
      ),
    ]);

    if (!visible) {
      this.#mobileSearchHeaderActionStates.clear();
    }

    elements.forEach((element) => {
      let state = this.#mobileSearchHeaderActionStates.get(element);

      if (!visible || !state) {
        const style = getComputedStyle(element);
        state = {
          opacity: style.opacity,
          transform: style.transform === 'none' ? 'translate3d(0, 0, 0)' : style.transform,
        };
        this.#mobileSearchHeaderActionStates.set(element, state);
      }

      const animation = element.animate(
        visible
          ? [
              { opacity: '0', transform: 'translate3d(0, -4px, 0)' },
              { opacity: state.opacity, transform: state.transform },
            ]
          : [
              { opacity: state.opacity, transform: state.transform },
              { opacity: '0', transform: 'translate3d(0, -4px, 0)' },
            ],
        {
          duration: visible ? 220 : 160,
          easing: visible ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'cubic-bezier(0.4, 0, 1, 1)',
          fill: 'both',
        }
      );

      this.#mobileSearchHeaderActionAnimations.push(animation);

      if (visible) {
        animation.finished
          .then(() => {
            if (!this.#mobileSearchHeaderActionAnimations.includes(animation)) return;
            animation.cancel();
            this.#mobileSearchHeaderActionAnimations =
              this.#mobileSearchHeaderActionAnimations.filter((item) => item !== animation);

            if (this.#mobileSearchHeaderActionAnimations.length === 0) {
              this.#mobileSearchHeaderActionStates.clear();
            }
          })
          .catch(() => {
            // Cancellation is expected when Search is reopened during the handoff.
          });
      }
    });
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();
    const mobileSearch = this.#isMobileSearch();
    const shouldAutofocusImmediately =
      this.hasAttribute('data-immediate-autofocus') && window.matchMedia('(max-width: 749px)').matches;
    // Search uses a non-modal dialog at every breakpoint. This keeps the single
    // global brand mark above the animated surface. Desktop keeps the standard
    // scroll lock; mobile uses a viewport-sized dialog interaction layer.
    const shouldOpenNonModal = this.id === 'search-modal';

    const openDialog = () => {
      if (shouldOpenNonModal) {
        dialog.show();
        if (!mobileSearch) {
          lockScroll(dialog);
        }
      } else {
        lockScroll(dialog);
        dialog.showModal();
      }

      if (shouldAutofocusImmediately || shouldOpenNonModal) {
        const autofocusTarget = dialog.querySelector('[data-dialog-autofocus]');
        if (autofocusTarget instanceof HTMLElement) {
          // Keep focus in the original tap call stack so iOS opens the
          // keyboard. The native autofocus attribute is intentionally not
          // used: dialog.show() would run its own unsuppressed focus step and
          // scroll the document before focus({ preventScroll: true }) can run.
          // Force the newly opened fixed panel to receive layout first. Without
          // this read, Safari can calculate the focus target from its pre-open
          // position and nudge the visual viewport while showing the keyboard.
          if (mobileSearch) {
            autofocusTarget.getBoundingClientRect();
          }
          autofocusTarget.focus({ preventScroll: true });
        }
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    if (mobileSearch) {
      const openingRevision = ++this.#searchOpeningRevision;
      // Search can be entered either from the visible header or from the
      // already-open mobile menu. Remember the inherited shell state so the
      // matching close path restores MENU, SEARCH and CART exactly once.
      this.#restoreHeaderShellOnMobileSearchClose =
        document.documentElement.hasAttribute('data-stylist-header-shell-hidden');
      this.#setMobileSearchGeometry(dialog);
      dialog.dataset.stylistSearchStage = 'waiting';
      if (!this.#restoreHeaderShellOnMobileSearchClose) {
        this.#animateMobileSearchHeaderActions(false);
      }

      // Mobile Search owns a fixed viewport-sized interaction layer, so it
      // does not need to change document overflow or the sticky-header state.
      // Only the white panel inside that layer expands from the measured
      // Search row; the page and global logo remain in their current state.
      openDialog();

      requestAnimationFrame(() => {
        if (
          openingRevision !== this.#searchOpeningRevision ||
          !dialog.open ||
          dialog.classList.contains('dialog-closing')
        ) {
          return;
        }

        dialog.dataset.stylistSearchStage = 'opening';
      });
      return;
    }

    requestAnimationFrame(openDialog);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    const mobileSearch = this.#isMobileSearch();

    if (!dialog.open) return;
    this.#searchOpeningRevision += 1;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (mobileSearch) {
      dialog.dataset.stylistSearchStage = 'closing';

      // Dismiss the keyboard while the viewport-sized dialog still intercepts
      // the screen, then let the white child panel finish its own animation.
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && dialog.contains(activeElement)) {
        activeElement.blur();
      }
    }

    dialog.classList.add('dialog-closing');

    const closingAnimationTarget = mobileSearch
      ? dialog.querySelector('predictive-search-component') ?? dialog
      : dialog;

    await onAnimationEnd(closingAnimationTarget, undefined, {
      subtree: false,
    });

    if (!mobileSearch) {
      unlockScroll(dialog);
      scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    if (mobileSearch) {
      unlockScroll(dialog);
      if (this.#restoreHeaderShellOnMobileSearchClose) {
        await restoreMobileHeaderShell();
      } else {
        this.#animateMobileSearchHeaderActions(true);
      }
      this.#restoreHeaderShellOnMobileSearchClose = false;
    }
    dialog.classList.remove('dialog-closing');
    delete dialog.dataset.stylistSearchStage;
    this.#clearMobileSearchGeometry(dialog);

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the desktop search after the pointer actually leaves the visible
   * panel. A layered element such as the persistent logo does not close it
   * while the pointer remains inside the panel's geometry.
   *
   * @param {PointerEvent} event
   */
  closeDialogOnPointerLeave = (event) => {
    const { dialog } = this.refs;

    if (!dialog.open || dialog.classList.contains('dialog-closing')) return;
    if (!window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches) return;

    const bounds = dialog.getBoundingClientRect();
    const pointerIsInside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;

    if (!pointerIsInside) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
